import React from "react";
import PropTypes from "prop-types";

import { Grid, IconButton, TextField, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import DragList, { moveItem } from "components/ContentForms/DragList";

/**
 * Editor for the string lists a brand page holds — intro paragraphs,
 * "Why Choose ..." bullets, "Our Commitment" paragraphs.
 *
 * Each entry is its own field with move-up / move-down / remove controls, so the
 * order the admin sees is the order the website renders.
 */
const StringListField = ({
  label,
  helperText,
  values,
  onChange,
  placeholder,
  rows,
  maxItems,
  error,
  group,
}) => {
  const update = (index, value) => {
    const next = [...values];
    next[index] = value;
    onChange(next);
  };

  const add = () => {
    if (values.length >= maxItems) return;
    onChange([...values, ""]);
  };

  const remove = (index) => {
    onChange(values.filter((item, i) => i !== index));
  };

  const move = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= values.length) return;

    const next = [...values];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const reorder = (from, to) => onChange(moveItem(values, from, to));

  return (
    <MDBox>
      <MDTypography variant="button" fontWeight="medium">
        {label}
      </MDTypography>

      {helperText ? (
        <MDTypography variant="caption" color="text" display="block" mb={1}>
          {helperText}
        </MDTypography>
      ) : null}

      {error ? (
        <MDTypography variant="caption" color="error" display="block" mb={1}>
          {error}
        </MDTypography>
      ) : null}

      {values.length === 0 ? (
        <MDTypography variant="caption" color="text" display="block" mb={1}>
          Nothing added yet.
        </MDTypography>
      ) : null}

      <DragList group={group || `strings-${label}`} count={values.length} onReorder={reorder}>
        {(index, drag) => (
          // The index is the identity here: entries are plain strings and the
          // list is reordered in place, so there is no stabler key available.
          <Grid
            container
            spacing={1}
            alignItems="flex-start"
            key={`item-${index}`}
            mb={1}
            {...drag.rowProps}
          >
            <Grid item xs={12} sm={10}>
              <TextField
                fullWidth
                multiline={rows > 1}
                rows={rows > 1 ? rows : undefined}
                label={`${label} ${index + 1}`}
                value={values[index]}
                placeholder={placeholder}
                onChange={(event) => update(index, event.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <MDBox display="flex" alignItems="center" pt={1}>
                {drag.handle}

                {/* The span is required: a disabled button fires no events, so
                    Tooltip cannot listen to it directly. */}
                <Tooltip title="Move up">
                  <span>
                    <IconButton
                      size="small"
                      disabled={index === 0}
                      onClick={() => move(index, -1)}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title="Move down">
                  <span>
                    <IconButton
                      size="small"
                      disabled={index === values.length - 1}
                      onClick={() => move(index, 1)}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title="Remove">
                  <IconButton size="small" color="error" onClick={() => remove(index)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </MDBox>
            </Grid>
          </Grid>
        )}
      </DragList>

      <MDButton
        variant="outlined"
        color="info"
        size="small"
        startIcon={<AddIcon />}
        onClick={add}
        disabled={values.length >= maxItems}
      >
        Add {label.toLowerCase()}
      </MDButton>

      {values.length >= maxItems ? (
        <MDTypography variant="caption" color="text" display="block" mt={1}>
          Maximum of {maxItems} entries reached.
        </MDTypography>
      ) : null}
    </MDBox>
  );
};

StringListField.defaultProps = {
  helperText: "",
  placeholder: "",
  rows: 1,
  maxItems: 60,
  error: "",
  group: "",
};

StringListField.propTypes = {
  label: PropTypes.string.isRequired,
  helperText: PropTypes.string,
  values: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  rows: PropTypes.number,
  maxItems: PropTypes.number,
  error: PropTypes.string,
  // Keeps sibling lists on the same form from accepting each other's rows.
  // Defaults to the label, which is unique per form in practice.
  group: PropTypes.string,
};

export default StringListField;
